/**
 * Finding the words in a paragraph worth checking, and deciding whether one
 * is misspelled once a dictionary has had its say. Kept apart from the
 * editor plugin so it can be tested against a real dictionary in Node.
 */
import type { SpellLanguage } from './language';

export interface Speller {
    spell(word: string): boolean;
    suggest(word: string): string[];
}

export interface WordToken {
    word: string;
    start: number;
    end: number;
    /** First word of a sentence, where a capital letter says nothing */
    sentenceStart: boolean;
}

// Letters, joined by apostrophes or hyphens: "don't", "vendê-la", "bien-estar"
const WORD = /[\p{L}\p{M}]+(?:['’-][\p{L}\p{M}]+)*/gu;
// Things that look like words but aren't prose: links, addresses, handles
const NOT_PROSE = /(?:https?:\/\/|www\.)\S+|\S+@\S+\.\S+|\b[\w-]+(?:\.[\w-]+)*\.(?:com|net|org|app|io|dev|co|ai|edu|gov|cr|mx|ar|br|es)\b\S*/giu;
const OPENERS = /["“‘«([¿¡'\s—–-]/u;
const SENTENCE_END = /[.!?…:]/;

export function findWords(text: string): WordToken[] {
    const skip: Array<[number, number]> = [];
    for (const m of Array.from(text.matchAll(NOT_PROSE))) skip.push([m.index!, m.index! + m[0].length]);

    const tokens: WordToken[] = [];
    for (const m of Array.from(text.matchAll(WORD))) {
        const start = m.index!;
        const end = start + m[0].length;
        const word = m[0];
        if (skip.some(([a, b]) => start < b && end > a)) continue;
        const before = text[start - 1] ?? '';
        const after = text[end] ?? '';
        // Hashtags, @handles, snake_case, and words glued to numbers ("3er", "mp4")
        if (/[\d_@#]/.test(before) || /[\d_@]/.test(after)) continue;
        if (Array.from(word).length < 2) continue;
        // ALL CAPS is an acronym; a capital inside the word is a brand (TikTok, iPhone)
        const letters = word.replace(/['’-]/g, '');
        if (letters === letters.toUpperCase()) continue;
        if (/\p{Lu}/u.test(letters.slice(1)) && !word.includes('-')) continue;

        let i = start - 1;
        while (i >= 0 && OPENERS.test(text[i])) i--;
        const sentenceStart = i < 0 || SENTENCE_END.test(text[i]);
        tokens.push({ word, start, end, sentenceStart });
    }
    return tokens;
}

/**
 * English words that Spanish and Portuguese speakers write every day but
 * their dictionaries don't list yet. Plural forms are added below.
 */
const LOANWORDS = new Set(('app online offline email mail link blog post feed like streaming influencer ' +
    'startup smartphone software hardware hashtag selfie podcast wifi web chat bot spam login password ' +
    'emoji meme reel story stories scroll follower playlist youtuber gamer crush okay marketing show ' +
    'look outfit delivery spoiler trending tweet stream streamer fan freelance router').split(' ')
    .flatMap((w) => [w, `${w}s`]));

const DIMINUTIVES = ['ecitos', 'ecitas', 'ecito', 'ecita', 'citos', 'citas', 'cito', 'cita',
    'itos', 'itas', 'ito', 'ita', 'illos', 'illas', 'illo', 'illa'];

/**
 * Spanish makes a diminutive of almost anything (calientito, fresquita,
 * ahorita, cerquita) and the dictionary only lists a few. Undo the suffix,
 * respell the stem the way the suffix changed it (qu → c, gu → g, c → z),
 * and see whether the word underneath is real.
 */
function isSpanishDiminutive(word: string, speller: Speller): boolean {
    const lower = word.toLowerCase();
    for (const suffix of DIMINUTIVES) {
        if (!lower.endsWith(suffix)) continue;
        const stem = lower.slice(0, -suffix.length);
        if (stem.length < 2) continue;
        const stems = new Set([stem, stem.replace(/qu$/, 'c'), stem.replace(/gu$/, 'g'), stem.replace(/c$/, 'z')]);
        const plural = suffix.endsWith('s');
        const endings = plural ? ['s', 'os', 'as', 'es'] : ['', 'o', 'a', 'e'];
        for (const s of Array.from(stems)) {
            for (const ending of endings) {
                if (speller.spell(s + ending)) return true;
            }
        }
    }
    return false;
}

export function isMisspelled(token: WordToken, lang: SpellLanguage, speller: Speller): boolean {
    const word = token.word.replace(/’/g, "'");
    if (speller.spell(word)) return false;
    if (lang !== 'en' && LOANWORDS.has(word.toLowerCase())) return false;
    if (word.includes('-') && word.split('-').every((part) => part.length < 2 || speller.spell(part))) return false;
    if (lang === 'es' && isSpanishDiminutive(word, speller)) return false;
    // A capitalized word mid-sentence is a name the dictionary can't know
    // (Instagram, Guanacaste). At the start of a sentence it gets no pass.
    if (!token.sentenceStart && /^\p{Lu}/u.test(word)) return false;
    return true;
}
